import React from 'react';
import { Paper, Typography, Box, Stack, Skeleton } from '@mui/material';

const StructureDetails = React.memo(({ data }) => {
  const isLoading = !data;

  return (
    <Paper
      elevation={1}
      sx={{
        flex: 1,
        p: 3,
        borderRadius: '9px',
        maxWidth: 260,
        border: '1px solid lightgray',
        maxHeight: { xs: 'auto', md: 'calc(113vh - 200px)' },
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#555',
        },
      }}
    >
      <Stack spacing={2}>
        <Typography variant='subtitle1' fontWeight={600} gutterBottom>
          Structure details
        </Typography>

        {isLoading ? (
          <>
            {/* Purlin length */}
            <Box>
              <Skeleton variant='text' width={100} height={20} />
              <Skeleton variant='text' width={80} height={20} />
            </Box>

            {/* Rafter length */}
            <Box>
              <Skeleton variant='text' width={100} height={20} />
              <Skeleton variant='text' width={80} height={20} />
            </Box>

            {/* Leg length */}
            <Box>
              <Skeleton variant='text' width={100} height={20} />
              <Skeleton variant='text' width={80} height={20} />
              <Skeleton variant='text' width={80} height={20} />
            </Box>

            {/* Distances */}
            <Box>
              <Skeleton variant='text' width={100} height={20} />
              <Skeleton variant='text' width={120} height={20} />
              <Skeleton variant='text' width={120} height={20} />
            </Box>

            {/* Area coverage */}
            <Box>
              <Skeleton variant='text' width={100} height={20} />
              <Skeleton variant='text' width={140} height={20} />
              <Skeleton variant='text' width={140} height={20} />
            </Box>

            {/* Panel ground clearance */}
            <Box>
              <Skeleton variant='text' width={140} height={20} />
              <Skeleton variant='text' width={120} height={20} />
              <Skeleton variant='text' width={120} height={20} />
            </Box>

            {/* Installation hardware */}
            <Box>
              <Skeleton variant='text' width={120} height={20} />
              <Skeleton variant='text' width={140} height={20} />
              <Skeleton variant='text' width={140} height={20} />
              <Skeleton variant='text' width={140} height={20} />
              <Skeleton variant='text' width={160} height={20} />
              <Skeleton variant='text' width={140} height={20} />
              <Skeleton variant='text' width={200} height={16} sx={{ mt: 1 }} />
            </Box>
          </>
        ) : (
          <>
            <Box>
              <Typography
                variant='body2'
                fontWeight={600}
                color='text.secondary'
              >
                Purlin length
              </Typography>
              <Typography variant='body2'>
                {data?.purlin_length?.length}m x {data?.purlin_length?.count}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant='body2'
                fontWeight={600}
                color='text.secondary'
              >
                Rafter length
              </Typography>
              <Typography variant='body2'>
                {data?.rafter_length?.length}m x {data?.rafter_length?.count}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant='body2'
                fontWeight={600}
                color='text.secondary'
              >
                Leg length
              </Typography>
              {data?.leg_heights &&
                Object.entries(data.leg_heights).map(([height, count]) => (
                  <Typography key={height} variant='body2'>
                    {height}m x {count}
                  </Typography>
                ))}
            </Box>
            <Box>
              <Typography
                variant='body2'
                fontWeight={600}
                color='text.secondary'
              >
                Distances
              </Typography>
              <Typography variant='body2'>
                North-South: {data?.north_to_south_leg_distance}m
              </Typography>
              <Typography variant='body2'>
                East-West: {data?.east_to_west_leg_distance}m
              </Typography>
            </Box>
            <Box>
              <Typography
                variant='body2'
                fontWeight={600}
                color='text.secondary'
              >
                Area coverage
              </Typography>
              <Typography variant='body2'>
                Between legs: {data?.area_bw_legs}m²
              </Typography>
              <Typography variant='body2'>
                Panel span: {data?.panel_span_area}m²
              </Typography>
            </Box>
            <Box>
              <Typography
                variant='body2'
                fontWeight={600}
                color='text.secondary'
              >
                Panel ground clearance
              </Typography>
              <Typography variant='body2'>
                Length: {data?.panel_clearance_ground?.length}m
              </Typography>
              <Typography variant='body2'>
                Breadth: {data?.panel_clearance_ground?.breadth}m
              </Typography>
            </Box>
            <Box>
              <Typography
                variant='body2'
                fontWeight={600}
                color='text.secondary'
              >
                Installation hardware
              </Typography>
              <Typography variant='body2'>
                Mid clamps: {data?.clamps?.mid_clamps}
              </Typography>
              <Typography variant='body2'>
                End clamps: {data?.clamps?.end_clamps}
              </Typography>
              <Typography variant='body2'>
                Base plates: {data?.fasteners?.base_plates}
              </Typography>
              <Typography variant='body2'>
                Base plate fasteners: {data?.fasteners?.base_plate_fastners}
              </Typography>
              <Typography variant='body2'>
                Nut-bolt pairs: {data?.fasteners?.nut_bolt_pairs}
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 1, fontSize: '0.8rem' }}
              >
                (Rafter-leg:{' '}
                {data?.fasteners?.breakdown_nut_bolt_pairs?.rafter_to_legs},
                Rafter-purlin:{' '}
                {data?.fasteners?.breakdown_nut_bolt_pairs?.rafter_to_purlins})
              </Typography>
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
});

export default StructureDetails;
